import { publicConfig } from "@/configs/public-config";
import { TokenService } from "@/providers/tokens/tokens.service";
import {
  parseApiError,
  readErrorResponseBody,
  buildFetchError,
} from "@/shared/helpers/api-file-download.helper";
import { type IApiResponseError } from "@/shared/interfaces/api-response.interface";

import { ReportExportFormat, type IExportReportResult } from "./reports.interface";

export interface IFetchReportExportResult {
  data?: IExportReportResult;
  error?: IApiResponseError;
}

export function getExportFileExtension(format: ReportExportFormat): string {
  if (format === ReportExportFormat.EXCEL) {
    return "xlsx";
  }
  if (format === ReportExportFormat.CSV) {
    return "csv";
  }
  return "pdf";
}

export function getExportFilenameFallback(format: ReportExportFormat): string {
  return `report-${new Date().toISOString()}.${getExportFileExtension(format)}`;
}

export function getExportContentType(
  format: ReportExportFormat,
  responseContentType: string | null,
): string {
  if (responseContentType) {
    return responseContentType;
  }
  if (format === ReportExportFormat.EXCEL) {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  if (format === ReportExportFormat.CSV) {
    return "text/csv";
  }
  return "application/pdf";
}

function buildAuthHeaders(): HeadersInit {
  const accessToken = TokenService.getAccessToken();
  const headers: HeadersInit = {};
  if (accessToken) {
    headers[publicConfig.authHeader] = `Bearer ${accessToken}`;
  }
  return headers;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary);
}

export async function fetchFoundersLetterQrDataUrl(): Promise<{
  data?: { dataUrl: string };
  error?: IApiResponseError;
}> {
  const baseUrl = `${publicConfig.apiUrl}/${publicConfig.apiTag}`;
  const url = `${baseUrl}/report/executive-summary/founders-letter-qr`;

  try {
    const response = await fetch(url, { method: "GET", headers: buildAuthHeaders() });

    if (!response.ok) {
      const errorBody = await readErrorResponseBody(response);
      return { error: parseApiError(response, errorBody) };
    }

    const arrayBuffer = await response.arrayBuffer();
    const contentType = response.headers.get("Content-Type") ?? "image/png";
    const dataUrl = `data:${contentType};base64,${arrayBufferToBase64(arrayBuffer)}`;

    return { data: { dataUrl } };
  } catch (err) {
    return { error: buildFetchError(url, err) };
  }
}

export async function fetchAuthenticatedReportExportPost(
  url: string,
  format: ReportExportFormat,
  body: unknown,
  resolveFilename: (contentDisposition: string | null, format: ReportExportFormat) => string,
): Promise<IFetchReportExportResult> {
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        ...buildAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorBody = await readErrorResponseBody(response);
      return { error: parseApiError(response, errorBody) };
    }

    const arrayBuffer = await response.arrayBuffer();
    const contentType = getExportContentType(
      format,
      response.headers.get("Content-Type"),
    );
    const blob = new Blob([arrayBuffer], { type: contentType });
    const contentDisposition = response.headers.get("Content-Disposition");
    const filename = resolveFilename(contentDisposition, format);

    return { data: { blob, filename } };
  } catch (err) {
    return { error: buildFetchError(url, err) };
  }
}

export async function fetchAuthenticatedReportExport(
  url: string,
  format: ReportExportFormat,
  resolveFilename: (contentDisposition: string | null, format: ReportExportFormat) => string,
): Promise<IFetchReportExportResult> {
  try {
    const response = await fetch(url, { method: "GET", headers: buildAuthHeaders() });

    if (!response.ok) {
      const errorBody = await readErrorResponseBody(response);
      return { error: parseApiError(response, errorBody) };
    }

    const arrayBuffer = await response.arrayBuffer();
    const contentType = getExportContentType(
      format,
      response.headers.get("Content-Type"),
    );
    const blob = new Blob([arrayBuffer], { type: contentType });
    const contentDisposition = response.headers.get("Content-Disposition");
    const filename = resolveFilename(contentDisposition, format);

    return { data: { blob, filename } };
  } catch (err) {
    return { error: buildFetchError(url, err) };
  }
}
